/*
 * custom-cart-addons.js
 * ---------------------------------------------------------------------------
 * Interactive behavior for the V3-parity complementary-product cart add-ons
 * (snippets/custom-cart-addons.liquid).
 *
 * - Variant select reveals + sets the card's Add button (the button animates in
 *   below the dropdown; choosing the placeholder collapses it again).
 * - Add posts to /cart/add.js with the cart-items-component section ids, then
 *   dispatches Horizon's CartAddEvent (the documented public seam) so
 *   CartItemsComponent.handleCartUpdate morphs the cart section -- re-rendering
 *   the line items, the add-on block (which re-aggregates and dedups the just
 *   added product out), and the header cart count -- without closing the drawer.
 *   We never hand-swap DOM; the morph does the rendering.
 *
 * Document-level delegation, bound once per page load, so it survives every
 * morph and covers both the drawer and the cart-page cart-items-component.
 * Mirrors V3 assets/cart.js (handleAddonSelectChange / handleAddonAddToCart /
 * addAddonToCart / resetAddonButtonState) and Horizon's product-form.js dispatch.
 */
import { CartAddEvent } from '@theme/events';

if (!window.__bactrackCartAddonsBound) {
  window.__bactrackCartAddonsBound = true;

  const ADDON_SELECT = '[data-addon-select]';
  const ADDON_BUTTON = '[data-addon-add-to-cart]';
  const CARD = '[data-cart-addon]';
  let inFlight = false;

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const blockOf = (el) => el.closest('[data-custom-cart-addons]');

  const clearError = (card) => {
    const err = card.querySelector('[data-addon-error]');
    if (err) err.textContent = '';
  };

  const showError = (card, message) => {
    const err = card.querySelector('[data-addon-error]');
    if (!err) return;
    const block = blockOf(card);
    const fallback = (block && block.dataset.errorText) || 'Sorry, we could not add that item.';
    err.textContent = message || fallback;
  };

  const setBusy = (btn, busy) => {
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');
    } else {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-loading');
    }
  };

  // Announce AFTER the morph. The morph re-renders the block (clearing the
  // in-block live region), so setting the text on the freshly rendered region a
  // moment later gives the screen reader a clean mutation to read. If the add
  // emptied the block (no add-ons left), there is no live region and the
  // announcement is skipped -- the morph (card gone, count updated) is the
  // primary feedback.
  const announceAfterMorph = (title, addedText) => {
    setTimeout(() => {
      const live = document.querySelector('[data-custom-cart-addons] [data-addon-live]');
      if (live) live.textContent = title ? title + ': ' + addedText : addedText;
    }, 200);
  };

  // Variant select -> set the button variant id and reveal/collapse the button.
  document.addEventListener('change', (event) => {
    const select = event.target.closest(ADDON_SELECT);
    if (!select) return;
    const card = select.closest(CARD);
    if (!card) return;
    const btn = card.querySelector(ADDON_BUTTON);
    const wrap = card.querySelector('.custom-cart-addon__btn-wrap');
    clearError(card);
    if (select.value) {
      if (btn) btn.setAttribute('data-variant-id', select.value);
      if (wrap) wrap.classList.add('is-visible');
    } else {
      if (btn) btn.removeAttribute('data-variant-id');
      if (wrap) wrap.classList.remove('is-visible');
    }
  });

  // Add button (delegated). The inFlight guard debounces double-clicks and
  // blocks overlapping adds across cards while a request is in flight.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest(ADDON_BUTTON);
    if (!btn) return;
    event.preventDefault();
    if (inFlight || btn.getAttribute('aria-busy') === 'true') return;
    const card = btn.closest(CARD);
    if (!card) return;
    // Multi-variant cards require an explicit selection (the button is collapsed
    // until then, so this is belt-and-suspenders).
    const select = card.querySelector(ADDON_SELECT);
    if (select && !select.value) return;
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    addToCart(btn, card, variantId);
  });

  function addToCart(btn, card, variantId) {
    inFlight = true;
    setBusy(btn, true);
    clearError(card);

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then((result) => {
        // Shopify returns a `status` field (422/etc.) on add failures.
        if (!result.ok || result.data.status) {
          showError(card, result.data.description || result.data.message);
          setBusy(btn, false);
          inFlight = false;
          return null;
        }
        const sections = result.data.sections;
        // Fetch the full cart so the dispatched event carries the resource +
        // item count (the header cart bubble reads these), mirroring product-form.js.
        return fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            // Capture the title + added-text BEFORE dispatch, while the card is
            // still attached (the morph detaches it).
            const titleEl = card.querySelector('.custom-cart-addon__title');
            const title = titleEl ? titleEl.textContent.trim() : '';
            const block = blockOf(card);
            const addedText = (block && block.dataset.addedText) || 'Added to cart';
            // Dispatch from the button (a child of cart-items-component), NOT the
            // component itself -- handleCartUpdate skips events where target === this.
            btn.dispatchEvent(
              new CartAddEvent(cart, variantId, {
                source: 'custom-cart-addons',
                itemCount: cart.item_count,
                sections: sections,
              })
            );
            // The morph re-renders the card subtree; resetting btn here is harmless
            // if it has already been replaced.
            inFlight = false;
            announceAfterMorph(title, addedText);
          });
      })
      .catch(() => {
        showError(card);
        setBusy(btn, false);
        inFlight = false;
      });
  }
}
